const THERMOSTAT_IMG =
  "https://customer-assets.emergentagent.com/job_thermal-design-3/artifacts/dp0jhx1o_zigbee-smart-thermostat-gaswater-heater-room-thermostat-digital-programmable-855434.webp";

export default function Control() {
  return (
    <section id="control" data-testid="control-section" className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SmartControlsSection />
        <AppSection />
      </div>
    </section>
  );
}

function SmartControlsSection() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-7">
        <div className="overline mb-6">Control</div>
        <h2
          data-testid="control-headline"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.05] text-ecoh-ink"
        >
          Smart controls &amp; building integration.
        </h2>
        <div className="mt-10 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
          <p>
            ecoHeatwave is designed to integrate seamlessly with modern smart
            controls infrastructure, enabling precise zone-by-zone heat
            management tailored to occupancy, usage patterns, and building
            performance requirements.
          </p>
          <p>
            Compatible with smart thermostats, zoned heating controls, and wider
            building energy management systems, ecoHeatwave supports responsive
            control strategies that improve comfort while optimising energy use
            across the building.
          </p>
          <p>
            This intelligent control capability allows heating output to be
            managed efficiently by room, time of day, or user demand—reducing
            unnecessary energy consumption while maintaining a consistently
            comfortable indoor environment.
          </p>
          <p>
            For developers and building operators, this creates the flexibility
            to integrate heating as part of a wider connected ecosystem,
            alongside lighting, security, assisted living technologies, and
            renewable energy systems.
          </p>
          <p className="font-medium text-ecoh-ink">
            The result is a heating solution designed not only for thermal
            comfort, but for smarter building performance.
          </p>
        </div>
      </div>
      <ThermostatImage />
    </div>
  );
}

function ThermostatImage() {
  return (
    <div className="lg:col-span-5 lg:sticky lg:top-28">
      <div className="relative rounded-2xl overflow-hidden bg-ecoh-surface aspect-square">
        <img
          src={THERMOSTAT_IMG}
          alt="Smart thermostat compatible with ecoHeatwave radiant heating systems"
          data-testid="control-thermostat-image"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
        />
      </div>
      <p className="mt-3 text-xs tracking-[0.2em] uppercase text-ecoh-muted">
        ecoHeatwave smart thermostat &amp; energy management systems
      </p>
    </div>
  );
}

function AppSection() {
  return (
    <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <AppCopy />
      <PhoneMockup />
    </div>
  );
}

function AppCopy() {
  return (
    <div className="lg:col-span-7">
      <div className="overline mb-6">ecoHeatwave App</div>
      <h3
        data-testid="control-app-headline"
        className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink"
      >
        Bringing heating, security, and smart home control together in
        one easy-to-use platform.
      </h3>
      <p className="mt-10 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        The ecoHeatwave App gives you complete control of your heating
        system from anywhere — allowing you to adjust temperatures,
        create schedules, monitor energy usage, and manage multiple
        thermostats directly from your smartphone.
      </p>
      <p className="mt-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        Built on the reliable Smart Life platform, the ecoHeatwave App
        also connects seamlessly with a wide range of smart home
        devices, including smart plugs, lighting, sensors, switches,
        security systems, smart cameras, and voice assistants such as
        Amazon Alexa and Google Assistant.
      </p>
      <p className="mt-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        And of course, each thermostat can also be operated manually
        if preferred.
      </p>
      <p className="mt-6 font-medium text-ecoh-ink text-base md:text-lg leading-relaxed">
        A fully integrated smart home experience — maximising comfort,
        convenience, security, and energy efficiency throughout your
        home or business.
      </p>
    </div>
  );
}

const PHONE_TILES = [
  { label: "Living", value: "21.5°", bg: "bg-white ring-1 ring-ecoh-ink/8" },
  { label: "Kitchen", value: "20.8°", bg: "bg-white ring-1 ring-ecoh-ink/8" },
  { label: "Bedroom", value: "19.0°", bg: "bg-ecoh-surface" },
  { label: "Energy", value: "2.3 kWh", bg: "bg-ecoh-clay" },
];

function PhoneMockup() {
  return (
    <div className="lg:col-span-5">
      <div
        data-testid="control-app-mockup"
        className="relative mx-auto w-[200px] sm:w-[220px] aspect-[9/19] rounded-[2rem] bg-ecoh-ink p-2 shadow-xl"
      >
        <div className="absolute inset-2 rounded-[1.6rem] bg-ecoh-bg flex flex-col items-center justify-center overflow-hidden">
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-14 h-4 rounded-full bg-ecoh-ink/90" />
          <svg viewBox="0 0 32 32" className="w-12 h-12 mt-4">
            <circle cx="16" cy="16" r="13" fill="none" stroke="#F65900" strokeWidth="1.8" />
            <circle cx="16" cy="16" r="8.5" fill="none" stroke="#F65900" strokeWidth="1.8" />
            <circle cx="16" cy="16" r="3.5" fill="#F65900" />
          </svg>
          <div className="mt-4 font-display text-ecoh-ink text-base tracking-tight">
            <span className="font-light">eco</span>
            <span className="font-medium">Heatwave</span>
          </div>
          <p className="mt-1.5 text-[9px] tracking-[0.2em] uppercase text-ecoh-muted">
            Smart home control
          </p>
          <div className="mt-5 w-[82%] grid grid-cols-2 gap-2">
            {PHONE_TILES.map((t) => (
              <div
                key={t.label}
                className={`h-11 rounded-lg ${t.bg} flex flex-col justify-center px-2`}
              >
                <span className="text-[8px] tracking-[0.16em] uppercase text-ecoh-muted">
                  {t.label}
                </span>
                <span className="text-xs font-medium text-ecoh-ink">{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-xs tracking-[0.2em] uppercase text-ecoh-muted">
        ecoHeatwave App · Preview
      </p>
    </div>
  );
}
